<template>
	<div
		ref="containerRef"
		:class="[
			'rounded-xl  px-4 scrollbar-hide relative transition-all duration-300 ease-in-out',
			{
				'max-h-[472px]  xl:max-h-[472px]  overflow-y-auto border':
					props.alternative,
				' bg-white border-slate-100 border': !props.alternative && !isQuotePage,
				'border-[#3B82F633] bg-[#0C1F53]': props.alternative,
				'bg-white border border-slate-100 lg:border-none': isQuotePage,
				'overflow-hidden': !props.alternative && !showAll,
				'h-full overflow-auto': showAll
			}
		]"
		:style="
			!props.alternative && stocksStore.top_gainers_stocks.length > 3
				? { height: !showAll ? '360px' : 'auto' }
				: {}
		"
	>
		<div
			ref="titleRef"
			:class="[
				'py-4 sticky top-0 border-b z-8 ',
				{
					'bg-white border-slate-100': !props.alternative,
					'!bg-[#0C1F53] border-[#3B82F633] !text-white': props.alternative
				}
			]"
		>
			<h2
				v-if="props.alternative"
				class="text-slate-900 leading-tight font-medium text-2xl text-white title-font"
			>
				Top Gainers
			</h2>
			<Subtitle
				v-else
				weight="medium"
				size="small"
				tag="h3"
				class="title-font"
			>
				Top Gainers
			</Subtitle>
		</div>

		<!-- Show skeleton while loading -->
		<TopGainerStocksSkeleton
			v-if="
				stocksStore.top_gainers_stocks.length === 0 &&
				!stocksStore.top_gainers_loaded
			"
			:item-count="10"
			:alternative="props.alternative"
			:is-quote-page="props.isQuotePage"
			size="sm"
			:show-all="showAll"
		/>

		<!-- Show static skeleton when loaded but no data (preserves dimensions) -->
		<TopGainerStocksSkeleton
			v-else-if="
				stocksStore.top_gainers_stocks.length === 0 &&
				stocksStore.top_gainers_loaded
			"
			:item-count="10"
			:alternative="props.alternative"
			:is-quote-page="props.isQuotePage"
			size="sm"
			:show-all="showAll"
			:static-mode="true"
		/>

		<!-- Show actual content when loaded -->
		<div
			v-else
			class="stocks-container gap-4 md:grid grid-cols-2 lg:block"
		>
			<div
				v-for="(stock, idx) in stocksStore.top_gainers_stocks.slice(0, 10)"
				:key="stock.SYMBOL"
				ref="stockItemRefs"
			>
				<NuxtLink :to="`/quote/${stock.SYMBOL}/`">
					<TopGainerBlock
						:theme="props.alternative ? 'dark' : 'light'"
						size="sm"
						:symbol="stock.SYMBOL"
						:net_chg="stock.NET_CHG"
						:price="stock.BID"
						:pc_chg="stock.PC_CHG"
						:name="stock.NAME"
						:labels="stock.labels"
						:prices="stock.prices"
						:class="
							idx === stocksStore.top_gainers_stocks.length - 1 ? '!border-b-0' : ''
						"
					/>
				</NuxtLink>
			</div>
		</div>

		<ShowMore
			v-if="stocksStore.top_gainers_stocks.length > 3 && !props.alternative"
			class="z-10"
			:class="{ relative: showAll }"
			:alternative="props.alternative"
			variant="primary"
			:is-expanded="showAll"
			@click="toggleShowMore"
		/>
	</div>
</template>

<script setup lang="ts">
import TopGainerBlock from "@/components/markets/TopGainerBlock.vue";
import { useStocksStore } from "@/stores/useStocksStore";

const props = defineProps({
	alternative: {
		type: Boolean,
		default: false
	},
	isQuotePage: {
		type: Boolean,
		default: false
	}
});

const stocksStore = useStocksStore();
const showAll = ref(false);

// Populate the store with top gainers on the client. The store guards against
// duplicate/concurrent requests, so it is safe to call from multiple instances.
onMounted(() => {
	stocksStore.fetchTopGainerStocks();
});

// Template refs
const containerRef = ref<HTMLElement>();
const titleRef = ref<HTMLElement>();
const stockItemRefs = ref<HTMLElement[]>([]);

// Reactive height calculation
const containerHeight = ref(360); // Default height

const calculateHeight = async () => {
	if (props.alternative) {
		return;
	}

	await nextTick(); // Ensure DOM is updated

	if (!titleRef.value || !stockItemRefs.value.length) {
		return;
	}

	// Get title height
	const titleHeight = titleRef.value.getBoundingClientRect().height;

	// Calculate visible items count
	const visibleItemsCount = showAll.value
		? stocksStore.top_gainers_stocks.length
		: Math.min(3, stocksStore.top_gainers_stocks.length);

	// Get height of a single stock item (they should be consistent)
	const singleItemHeight =
		stockItemRefs.value[0]?.getBoundingClientRect().height || 80;

	// Calculate total items height
	const itemsHeight = singleItemHeight * visibleItemsCount;

	const button = containerRef.value?.querySelector(".show-more-btn");

	const showMoreHeight = button?.getBoundingClientRect().height || 0;

	// Total calculated height
	const totalHeight = titleHeight + itemsHeight + (showMoreHeight - 16);

	containerHeight.value = totalHeight;
};

const toggleShowMore = async () => {
	showAll.value = !showAll.value;
};

watch(showAll, calculateHeight);
</script>

<style scoped lang="scss">
@use "~/assets/utils.scss" as *;

.text-black {
	color: #222222 !important;
}

/* Alternative theme overrides */
:deep(.alternative-theme) {
	color: white !important;
}

.stocks-container a div.text-white.border-b.py-3.border-slate-200 {
	max-width: 100% !important;
}
</style>
